import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  BadgeCheck,
  Bell,
  Building2,
  CheckCheck,
  ExternalLink,
  FileText,
  IdCard,
  Loader2,
  MessageSquare,
  Send,
  Stethoscope,
  Trash2,
  UserPlus,
} from "lucide-react";

import { ConnectionStatusBadge } from "@/components/admin/notifications/connection-status-badge";
import { EventDetailSheet } from "@/components/admin/notifications/event-detail-sheet";
import {
  LiveEventFeed,
  type LiveEventTypeMeta,
} from "@/components/admin/notifications/live-event-feed";
import type { LiveEvent, LiveEventPayload } from "@/components/admin/notifications/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminFetch } from "@/hooks/use-admin-fetch";
import { useAdminContext } from "@/lib/admin-context";
import { normalizeNotificationLink } from "@/lib/notification-link";
import { useRealtimeStream } from "@/lib/realtime/use-realtime-stream";

/**
 * Admin → Notifications.
 *
 * Port of `src/app/admin/notifications/page.tsx` (legacy NextJS, ~470 LOC) per D21.
 *
 * Endpoints:
 *   - GET   /api/account/notifications?workspaceType=admin&limit=&offset=&include_admin=true&unread=
 *   - GET   /api/account/notifications/unread-count?include_admin=true
 *   - PATCH /api/account/notifications/mark-read (id | all)
 */

interface Notification {
  id: string;
  type: string;
  title: Record<string, string>;
  body: Record<string, string>;
  data: Record<string, unknown> | null;
  channels: string[];
  status: string;
  readAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  "admin.provider_registered": {
    icon: <Stethoscope className="size-4" />,
    color: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300",
    label: "Provider Registration",
  },
  "admin.provider_publish": {
    icon: <Stethoscope className="size-4" />,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    label: "Provider Publish",
  },
  "admin.provider_claim": {
    icon: <Stethoscope className="size-4" />,
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
    label: "Provider Claim",
  },
  "admin.facility_claim": {
    icon: <Building2 className="size-4" />,
    color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300",
    label: "Facility Claim",
  },
  "admin.facility_registered": {
    icon: <Building2 className="size-4" />,
    color: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300",
    label: "Facility Registration",
  },
  "admin.facility_publish": {
    icon: <Building2 className="size-4" />,
    color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
    label: "Facility Publish",
  },
  "admin.feedback_submitted": {
    icon: <MessageSquare className="size-4" />,
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
    label: "Feedback",
  },
  "admin.provider_specialty_pending": {
    icon: <BadgeCheck className="size-4" />,
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
    label: "Specialty Pending",
  },
  "admin.provider_profession_changed": {
    icon: <IdCard className="size-4" />,
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
    label: "Profession Changed",
  },
  "admin.specialty_suggested": {
    icon: <BadgeCheck className="size-4" />,
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
    label: "Specialty Suggested",
  },
  "admin.service_suggested": {
    icon: <Send className="size-4" />,
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
    label: "Service Suggested",
  },
  "admin.account_deletion": {
    icon: <Trash2 className="size-4" />,
    color: "bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300",
    label: "Account Deletion",
  },
  "admin.provider_docs_changed": {
    icon: <FileText className="size-4" />,
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
    label: "Provider Docs Changed",
  },
  "admin.challenge_submitted": {
    icon: <UserPlus className="size-4" />,
    color: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
    label: "Challenge Submitted",
  },
  "admin.challenge_comment": {
    icon: <MessageSquare className="size-4" />,
    color: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
    label: "Challenge Comment",
  },
};

function timeAgo(dateStr: string, fmt: { now: string; min: string; hr: string; days: string }) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return fmt.now;
  if (mins < 60) return fmt.min.replace("{n}", String(mins));
  const hours = Math.floor(mins / 60);
  if (hours < 24) return fmt.hr.replace("{n}", String(hours));
  const days = Math.floor(hours / 24);
  if (days < 7) return fmt.days.replace("{n}", String(days));
  return new Date(dateStr).toLocaleDateString();
}

function extractCountry(n: Notification): string | null {
  if (n.data?.country) return String(n.data.country);
  const body = n.body?.ro || n.body?.en || "";
  const match = body.match(/,\s*([A-Z]{2})\s*$/);
  return match?.[1] || null;
}

export const Route = createFileRoute("/_authenticated/notifications")({
  component: NotificationsPage,
});

function NotificationsPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  // Header country selector drives the country filter; "all" when no header pick.
  // The local in-page country pills proxy through `setCountry` so toggling them
  // also updates the header URL search param (single source of truth).
  const { country: headerCountry, setCountry: setHeaderCountry } = useAdminContext();
  const filterCountry = headerCountry ?? "all";
  const setFilterCountry = (code: string) => setHeaderCountry(code === "all" ? null : code);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const [filterRead, setFilterRead] = useState<"all" | "unread" | "read">("all");
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [offset, setOffset] = useState(0);
  const limit = 30;

  // ---- Realtime live feed -------------------------------------------------
  // Cap the in-memory buffer to MAX_LIVE_EVENTS so an admin who leaves the tab
  // open for hours doesn't accumulate unbounded DOM nodes / Framer Motion subs.
  const MAX_LIVE_EVENTS = 50;
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<LiveEvent | null>(null);
  const [liveTypeFilter, setLiveTypeFilter] = useState<string>("all");
  const liveSeqRef = useRef(0);

  const stream = useRealtimeStream<LiveEventPayload>({
    ssePath: "/api/admin/notifications/stream",
    // wsPath/pollPath intentionally omitted — backend currently exposes SSE only.
    // Adding a pollPath later (e.g. "/api/admin/notifications/recent") will
    // automatically activate the polling fallback when SSE fails.
  });

  // Single subscriber pushes every received event into the buffer.
  // Internal "connected" / "service.degraded" frames from the backend are
  // discarded — they are protocol meta, not user-facing events.
  useEffect(() => {
    const unsubscribe = stream.subscribe((evt) => {
      if (!evt || !evt.type) return;
      if (evt.type === "connected" || evt.type === "service.degraded") return;
      const localId = evt.id ?? `${Date.now()}-${liveSeqRef.current++}`;
      const liveEvt: LiveEvent = {
        ...evt,
        localId,
        receivedAt: Date.now(),
      };
      setLiveEvents((prev) => {
        // Dedup by server id when present so reconnects don't double-render.
        if (evt.id && prev.some((p) => p.id === evt.id)) return prev;
        const next = [liveEvt, ...prev];
        return next.length > MAX_LIVE_EVENTS ? next.slice(0, MAX_LIVE_EVENTS) : next;
      });
    });
    return unsubscribe;
  }, [stream]);

  const liveTypeMeta = useCallback((type: string): LiveEventTypeMeta => {
    const cfg = TYPE_CONFIG[type];
    if (cfg) return cfg;
    return {
      icon: <FileText className="size-4" />,
      color: "bg-muted text-muted-foreground",
      label: type,
    };
  }, []);

  const liveTypePills = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of liveEvents) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count, meta: liveTypeMeta(type) }))
      .sort((a, b) => b.count - a.count);
  }, [liveEvents, liveTypeMeta]);

  const filteredLiveEvents = useMemo(
    () =>
      liveTypeFilter === "all" ? liveEvents : liveEvents.filter((e) => e.type === liveTypeFilter),
    [liveEvents, liveTypeFilter],
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      params.set("workspaceType", "admin");
      params.set("include_admin", "true");
      if (filterRead === "unread") params.set("unread", "true");

      const [data, countData] = await Promise.all([
        adminFetch.get<{ notifications: Notification[]; total: number }>(
          `/api/account/notifications?${params}`,
        ),
        adminFetch.get<{ count: number }>(
          "/api/account/notifications/unread-count?include_admin=true",
        ),
      ]);

      let list = data.notifications || [];
      setAllNotifications(list);
      if (filterType !== "all") list = list.filter((n) => n.type === filterType);
      if (filterCountry !== "all") {
        list = list.filter((n) => {
          const c = extractCountry(n);
          return !c || c === filterCountry;
        });
      }
      setNotifications(list);
      setTotal(data.total || 0);
      setUnreadCount(countData.count || 0);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [adminFetch, offset, filterRead, filterType, filterCountry]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await adminFetch.patch("/api/account/notifications/mark-read", { all: true });
      fetchNotifications();
    } catch {
      /* ignore */
    }
  };

  const markRead = async (id: string) => {
    try {
      await adminFetch.patch("/api/account/notifications/mark-read", { id });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      /* ignore */
    }
  };

  const availableCountries = Array.from(
    new Set(allNotifications.map(extractCountry).filter(Boolean) as string[]),
  ).sort();

  const fmt = {
    now: t`now`,
    min: t`{n} min ago`,
    hr: t`{n}h ago`,
    days: t`{n} days ago`,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">
            <Trans>Admin Notifications</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>All system notifications for administration</Trans>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConnectionStatusBadge
            status={stream.status}
            transport={stream.transport}
            paused={stream.paused}
            onReconnect={stream.reconnect}
            onTogglePause={() => stream.setPaused(!stream.paused)}
          />
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} className="gap-1.5">
              <CheckCheck className="size-4" />
              <Trans>Mark all read ({unreadCount})</Trans>
            </Button>
          )}
        </div>
      </div>

      <section
        aria-label="Live event stream"
        className="border-border bg-card/50 space-y-3 rounded-2xl border p-4"
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bell className="text-primary size-4" />
            <h2 className="text-foreground text-sm font-semibold">
              <Trans>Live stream</Trans>
            </h2>
            <span className="text-muted-foreground text-xs">
              <Trans>
                {filteredLiveEvents.length} of {liveEvents.length} events
              </Trans>
            </span>
          </div>
          {liveEvents.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                setLiveEvents([]);
                setLiveTypeFilter("all");
              }}
            >
              <Trans>Clear</Trans>
            </Button>
          )}
        </header>

        {liveTypePills.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={liveTypeFilter === "all"}
              onClick={() => setLiveTypeFilter("all")}
              className={`min-h-[1.75rem] rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                liveTypeFilter === "all"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40"
              }`}
            >
              <Trans>All</Trans>
              <span className="ml-1 text-[0.625rem] opacity-70">{liveEvents.length}</span>
            </button>
            {liveTypePills.map(({ type, count, meta }) => (
              <button
                key={type}
                type="button"
                role="tab"
                aria-selected={liveTypeFilter === type}
                onClick={() => setLiveTypeFilter(type)}
                className={`min-h-[1.75rem] rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  liveTypeFilter === type
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40"
                }`}
              >
                {meta.label}
                <span className="ml-1 text-[0.625rem] opacity-70">{count}</span>
              </button>
            ))}
          </div>
        )}

        <LiveEventFeed
          events={filteredLiveEvents}
          paused={stream.paused}
          onSelect={setSelectedEvent}
          typeMeta={liveTypeMeta}
        />
      </section>

      <EventDetailSheet
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        meta={selectedEvent ? liveTypeMeta(selectedEvent.type) : null}
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
              <Bell className="size-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{total}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Total</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900">
              <Bell className="size-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{unreadCount}</div>
              <div className="text-muted-foreground text-xs">
                <Trans>Unread</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900">
              <Stethoscope className="size-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {notifications.filter((n) => n.type.includes("provider")).length}
              </div>
              <div className="text-muted-foreground text-xs">
                <Trans>Providers</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900">
              <Building2 className="size-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {notifications.filter((n) => n.type.includes("facility")).length}
              </div>
              <div className="text-muted-foreground text-xs">
                <Trans>Facilities</Trans>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
          value={filterRead}
          onChange={(e) => {
            setFilterRead(e.target.value as "all" | "unread" | "read");
            setOffset(0);
          }}
        >
          <option value="all">{t`All states`}</option>
          <option value="unread">{t`Unread`}</option>
          <option value="read">{t`Read`}</option>
        </select>
        <select
          className="border-border bg-card focus:ring-primary/30 focus:border-primary rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setOffset(0);
          }}
        >
          <option value="all">{t`All types`}</option>
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>
              {cfg.label}
            </option>
          ))}
        </select>

        {availableCountries.length > 0 && (
          <>
            <div className="text-muted-foreground text-xs">|</div>
            <button
              type="button"
              onClick={() => {
                setFilterCountry("all");
                setOffset(0);
              }}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                filterCountry === "all"
                  ? "border-teal-500 bg-teal-50 text-teal-700 dark:border-teal-600 dark:bg-teal-950 dark:text-teal-300"
                  : "border-border bg-card text-muted-foreground hover:border-teal-300"
              }`}
            >
              <Trans>All countries</Trans>
            </button>
            {availableCountries.map((cc) => {
              const count = allNotifications.filter((n) => {
                const c = extractCountry(n);
                return !c || c === cc;
              }).length;
              return (
                <button
                  key={cc}
                  type="button"
                  onClick={() => {
                    setFilterCountry(cc);
                    setOffset(0);
                  }}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterCountry === cc
                      ? "border-teal-500 bg-teal-50 text-teal-700 dark:border-teal-600 dark:bg-teal-950 dark:text-teal-300"
                      : "border-border bg-card text-muted-foreground hover:border-teal-300"
                  }`}
                >
                  {cc}
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 min-w-[1rem] px-1 text-[0.625rem]"
                  >
                    {count}
                  </Badge>
                </button>
              );
            })}
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-gray-400" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="py-12 text-center">
          <Bell className="mx-auto size-10 text-gray-300 dark:text-gray-600" />
          <p className="text-muted-foreground mt-3 text-sm">
            <Trans>No notifications</Trans>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notif) => {
            const cfg = TYPE_CONFIG[notif.type] || {
              icon: <FileText className="size-4" />,
              color: "bg-gray-100 text-gray-600",
              label: notif.type,
            };
            const dataLink = notif.data?.link as string | undefined;
            const rawLink = dataLink ? normalizeNotificationLink(dataLink) : undefined;
            const country = extractCountry(notif);
            const link =
              rawLink && country && !rawLink.includes("country=")
                ? `${rawLink}${rawLink.includes("?") ? "&" : "?"}country=${country}`
                : rawLink;

            return (
              <div
                key={notif.id}
                className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${
                  notif.readAt
                    ? "border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
                    : "border-teal-200 bg-teal-50/50 dark:border-teal-800 dark:bg-teal-950/20"
                }`}
              >
                <div
                  className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${cfg.color}`}
                >
                  {cfg.icon}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p
                        className={`text-sm ${
                          notif.readAt
                            ? "text-gray-700 dark:text-gray-300"
                            : "font-semibold text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {notif.title?.ro || notif.title?.en || t`Notification`}
                      </p>
                      <p className="text-muted-foreground mt-0.5 text-sm">
                        {notif.body?.ro || notif.body?.en}
                      </p>
                    </div>
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {timeAgo(notif.createdAt, fmt)}
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="secondary" className="text-[0.625rem]">
                      {cfg.label}
                    </Badge>
                    {notif.channels.map((ch) => (
                      <Badge key={ch} variant="outline" className="text-[0.625rem]">
                        {ch}
                      </Badge>
                    ))}
                    <div className="ml-auto flex items-center gap-1">
                      {!notif.readAt && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => markRead(notif.id)}
                        >
                          <CheckCheck className="mr-1 size-3" />
                          <Trans>Read</Trans>
                        </Button>
                      )}
                      {link && (
                        <Link to={link} target="_self">
                          <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs">
                            <ExternalLink className="size-3" />
                            <Trans>Open</Trans>
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                {!notif.readAt && (
                  <div className="mt-2 size-2.5 shrink-0 rounded-full bg-teal-500" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            <Trans>
              {offset + 1}–{Math.min(offset + limit, total)} of {total}
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              <Trans>Previous</Trans>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              <Trans>Next</Trans>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
